/**
 * URL Context Store
 *
 * A singleton store that holds the current URL-based tenant/realm context.
 * This allows non-React code (like Redux thunks and API helpers) to access
 * URL context without needing React hooks.
 *
 * The store is synchronized with URL params by the UrlContextSynchronizer component.
 *
 * Priority:
 * 1. URL params (tenant, realmId) when present
 * 2. Falls back to undefined (ApiClientManager will use auth defaults)
 */

interface UrlContext {
  tenant: string | undefined;
  realmId: string | undefined;
}

class UrlContextStore {
  private context: UrlContext = {
    tenant: undefined,
    realmId: undefined,
  };

  /**
   * Update the URL context. Called by UrlContextSynchronizer when URL changes.
   */
  setContext(context: UrlContext): void {
    this.context = context;
  }

  /**
   * Get the current URL context.
   * Returns undefined values if no URL params are present.
   */
  getContext(): UrlContext {
    return this.context;
  }

  /**
   * Get tenant from URL params, or undefined if not in URL.
   */
  getTenant(): string | undefined {
    return this.context.tenant;
  }

  /**
   * Get realmId from URL params, or undefined if not in URL.
   */
  getRealmId(): string | undefined {
    return this.context.realmId;
  }

  /**
   * Check if context is from URL (vs auth defaults).
   */
  hasUrlContext(): boolean {
    return !!(this.context.tenant || this.context.realmId);
  }

  /**
   * Clear URL context (e.g., on navigation to a page without context params).
   */
  clear(): void {
    this.context = {
      tenant: undefined,
      realmId: undefined,
    };
  }
}

// Singleton instance
export const urlContextStore = new UrlContextStore();
